import { AcAppHeader, AcCard, AcCellPopover, AcLoader, AcSelect, AcTypography } from '@components'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useStore } from '@hooks/use-store'
import { toBuurtbase } from '@helpers/redirect-buurtbase-url'
import styles from './ac-week-match-score.module.scss'

const UNASSIGNED = '__unassigned__'
const ALL = '__all__'

type Fail = { reason: string; penalty: number }
type Row = {
  id: number
  score: number
  fails: Fail[]
  user_id: number | null
  user_name: string
  user_key: string
  order_id: number | null
  order_name: string
}

const decodeEntities = (input: string): string => {
  if (typeof document === 'undefined') return input
  const el = document.createElement('textarea')
  el.innerHTML = input
  return el.value
}

const scoreColor = (score: number): string => {
  if (score < 80) return '#dc2626'   // red
  if (score > 90) return '#16a34a'   // green
  return '#d97706'                    // amber 80-90
}

export const AcWeekMatchScore = observer(() => {
  const { id } = useParams()
  const { weeks } = useStore()
  const [userFilter, setUserFilter] = useState<string>(ALL)
  const [openIssuesRow, setOpenIssuesRow] = useState<number | null>(null)

  useEffect(() => {
    if (!id) return
    weeks.getMatchScore(id).then(res => {
      // eslint-disable-next-line no-console
      console.log('[match-score] response for week', id, res)
    })
    return () => {
      weeks.match_score = null
      weeks.match_score_week = null
    }
  }, [id])

  const rows: Row[] = useMemo(() => {
    const data = weeks.watch_match_score
    if (!Array.isArray(data)) return []
    return data.map((d: any) => {
      const name = d?.user?.full_name?.trim()
      return {
        id: d?.id,
        score: Number(d?.score ?? 0),
        fails: Array.isArray(d?.fails) ? d.fails : [],
        user_id: d?.user?.id ?? null,
        user_name: name || 'Niet toegewezen',
        user_key: d?.user?.id ? String(d.user.id) : UNASSIGNED,
        order_id: d?.order?.id ?? null,
        order_name: decodeEntities(d?.order?.name ?? '-'),
      }
    })
  }, [weeks.watch_match_score])

  const userOptions = useMemo(() => {
    const seen = new Map<string, { name: string; count: number }>()
    rows.forEach(r => {
      const entry = seen.get(r.user_key)
      if (entry) entry.count += 1
      else seen.set(r.user_key, { name: r.user_name, count: 1 })
    })
    const list = Array.from(seen.entries())
      .map(([id, { name, count }]) => ({ id, label: `${name} (${count})` }))
      .sort((a, b) => {
        if (a.id === UNASSIGNED) return 1
        if (b.id === UNASSIGNED) return -1
        return a.label.localeCompare(b.label)
      })
    return [{ id: ALL, label: `Alle gebruikers (${seen.size})` }, ...list]
  }, [rows])

  const visibleRows = useMemo(() => {
    const filtered = userFilter === ALL ? rows : rows.filter(r => r.user_key === userFilter)
    return [...filtered].sort((a, b) => a.score - b.score)
  }, [rows, userFilter])

  const stats = useMemo(() => {
    const total = visibleRows.length
    if (total === 0) return { total: 0, avg: 0, withIssues: 0, perfect: 0 }
    const sum = visibleRows.reduce((s, r) => s + r.score, 0)
    const withIssues = visibleRows.filter(r => r.fails.length > 0).length
    const perfect = visibleRows.filter(r => r.score === 100).length
    return { total, avg: Math.round(sum / total), withIssues, perfect }
  }, [visibleRows])

  const data = weeks.watch_match_score
  const week = weeks.watch_match_score_week

  const title = week
    ? `Match score · week ${week.week} - ${week.year}`
    : `Match score · week ${id}`

  return (
    <>
      <AcAppHeader
        title={title}
        showBackButton
        action={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 }}>
            <AcTypography element="span" size="sm">Gebruiker</AcTypography>
            <AcSelect
              key={userOptions.length}
              name="user-filter"
              id="match-score-user-filter"
              options={userOptions}
              valueKey="id"
              labelKey="label"
              defaultValue={userOptions.find(o => o.id === userFilter)}
              onChange={(opt: any) => setUserFilter(opt?.id ?? ALL)}
              placeholder="Alle gebruikers"
              noMinWidth
            />
          </div>
        }
      />
      {data && visibleRows.length > 0 && (
        <AcCard>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
            <div>
              <AcTypography weight="semibold">Gemiddelde</AcTypography>
              <div>
                <span
                  style={{
                    display: 'inline-block',
                    minWidth: 40,
                    padding: '2px 8px',
                    borderRadius: 12,
                    background: scoreColor(stats.avg),
                    color: '#fff',
                    fontSize: 12,
                    fontWeight: 600,
                    textAlign: 'center',
                  }}
                >
                  {stats.avg}
                </span>
              </div>
            </div>
            <div>
              <AcTypography weight="semibold">Orders</AcTypography>
              <div>
                <AcTypography element="span" size="sm">{stats.total}</AcTypography>
              </div>
            </div>
            <div>
              <AcTypography weight="semibold">Met issues</AcTypography>
              <div>
                <AcTypography element="span" size="sm">{stats.withIssues}</AcTypography>
              </div>
            </div>
            <div>
              <AcTypography weight="semibold">Perfect (100)</AcTypography>
              <div>
                <AcTypography element="span" size="sm">{stats.perfect}</AcTypography>
              </div>
            </div>
          </div>
        </AcCard>
      )}
      <AcCard>
        {weeks.is_loading && !data ? (
          <AcLoader />
        ) : visibleRows.length === 0 ? (
          <AcTypography>-</AcTypography>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '8px 6px' }}>Order</th>
                  <th style={{ textAlign: 'left', padding: '8px 6px' }}>Gebruiker</th>
                  <th style={{ textAlign: 'left', padding: '8px 6px', width: 90 }}>Score</th>
                  <th style={{ textAlign: 'left', padding: '8px 6px' }}>Issues</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map(r => (
                  <tr key={r.id} style={{ borderTop: '1px solid #eee' }}>
                    <td style={{ padding: '8px 6px', verticalAlign: 'top' }}>
                      {r.order_id ? (
                        <a
                          className={styles['order-link']}
                          href={toBuurtbase(`/order/${r.order_id}`)}
                        >
                          <AcTypography size="sm" element="span" weight="semibold">
                            {r.order_name}
                          </AcTypography>
                        </a>
                      ) : (
                        <AcTypography size="sm" element="span" weight="semibold">
                          {r.order_name}
                        </AcTypography>
                      )}
                    </td>
                    <td style={{ padding: '8px 6px', verticalAlign: 'top' }}>
                      <AcTypography size="sm" element="span" weight="light">
                        {r.user_name}
                      </AcTypography>
                    </td>
                    <td style={{ padding: '8px 6px', verticalAlign: 'top' }}>
                      <span
                        style={{
                          display: 'inline-block',
                          minWidth: 40,
                          padding: '2px 8px',
                          borderRadius: 12,
                          background: scoreColor(r.score),
                          color: '#fff',
                          fontSize: 12,
                          fontWeight: 600,
                          textAlign: 'center',
                        }}
                      >
                        {r.score}
                      </span>
                    </td>
                    <td style={{ padding: '8px 6px', verticalAlign: 'top' }}>
                      {r.fails.length === 0 ? (
                        <AcTypography size="sm" element="span" weight="light">-</AcTypography>
                      ) : (
                        <AcCellPopover
                          open={openIssuesRow === r.id}
                          onToggle={() => setOpenIssuesRow(openIssuesRow === r.id ? null : r.id)}
                          label={
                            <AcTypography size="sm" element="span" weight="light">
                              {r.fails.length} {r.fails.length === 1 ? 'issue' : 'issues'}
                            </AcTypography>
                          }
                        >
                          <ul style={{ margin: 0, paddingLeft: 16 }}>
                            {r.fails.map((f, i) => (
                              <li key={i}>
                                <AcTypography size="sm" element="span" weight="light">
                                  {f.reason} <span style={{ color: '#888' }}>(-{f.penalty})</span>
                                </AcTypography>
                              </li>
                            ))}
                          </ul>
                        </AcCellPopover>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AcCard>
    </>
  )
})
